import { Link } from "react-router-dom";
import { Phone, Mail, MapPin, Clock, Instagram, MessageCircle, ArrowRight, Send } from "lucide-react";
import { Button } from "@/components/ui/button";
import PageHero from "@/components/shared/PageHero";
import { useSite } from "@/contexts/SiteContext";
import LeadForm from "@/components/shared/LeadForm";

export default function Contact() {
  const { settings } = useSite();
  const wa = settings.whatsapp?.replace(/[^0-9]/g, "") || "918799757375";

  return (
    <div>
      <PageHero
        label="Contact Us"
        title="Let's start a wholesale conversation."
        subtitle="Reach out for catalogues, bulk quotations, dealer onboarding, or a warehouse visit. A dedicated business executive will respond within 24 hours."
        breadcrumbs={[{ label: "Contact" }]}
      />

      <section className="py-14">
        <div className="mx-auto max-w-7xl px-4 md:px-8 grid lg:grid-cols-2 gap-12">
          <div>
            <p className="section-label mb-4"><span className="inline-block w-8 h-px bg-gold align-middle mr-3" />Reach Us Directly</p>
            <h2 className="serif-display text-3xl md:text-4xl text-foreground">Multiple ways to connect.</h2>

            <div className="mt-8 space-y-5">
              {[
                { icon: MapPin, l: "Corporate Office", v: settings.address, a: { href: settings.google_maps_url, label: "Get Directions", target: "_blank" } },
                { icon: Phone, l: "Sales Hotline", v: settings.phone_primary, a: { href: `tel:${settings.phone_primary}`, label: "Call Now" }, extra: settings.phone_secondary ? { href: `tel:${settings.phone_secondary}`, v: settings.phone_secondary } : null },
                { icon: Mail, l: "Business Email", v: settings.email, a: { href: `mailto:${settings.email}`, label: "Send Email" } },
                { icon: MessageCircle, l: "WhatsApp Business", v: settings.phone_primary, a: { href: `https://wa.me/${wa}`, label: "Chat on WhatsApp", target: "_blank" } },
                { icon: Clock, l: "Business Hours", v: settings.business_hours },
                { icon: Instagram, l: "Instagram", v: "@hermanos_wholesale", a: settings.social_instagram ? { href: settings.social_instagram, label: "Follow", target: "_blank" } : null },
              ].map((c, i) => (
                <div key={i} className="flex gap-4 p-5 rounded-md border border-border/60 bg-card hover:border-gold/60 transition-colors">
                  <div className="h-11 w-11 rounded-sm bg-gold/10 text-gold flex items-center justify-center shrink-0">
                    <c.icon size={18} strokeWidth={1.5} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="section-label text-[0.6rem]">{c.l}</p>
                    <p className="mt-1 text-foreground break-words">{c.v}</p>
                    {c.extra && <p className="mt-0.5 text-foreground/70 text-sm"><a href={c.extra.href} className="hover:text-gold">{c.extra.v}</a></p>}
                    {c.a && (
                      <a href={c.a.href} target={c.a.target} rel={c.a.target ? "noreferrer" : undefined} className="mt-2 inline-flex items-center gap-1 text-xs tracking-wider uppercase text-gold hover:underline">
                        {c.a.label} <ArrowRight size={11} />
                      </a>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div>
            <div className="p-8 rounded-md border border-border/60 bg-card">
              <p className="section-label mb-3">Business Enquiry Form</p>
              <h3 className="serif-display text-3xl text-foreground">Send us a message.</h3>
              <p className="mt-2 text-sm text-foreground/70">Fill in your business details — our sales team responds within one working day.</p>
              <div className="mt-6">
                <LeadForm type="contact" showFields={["company_name", "contact_person", "phone", "email", "business_type", "state", "city", "message"]} submitLabel="Send Enquiry" />
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-14 border-t border-border/60 bg-secondary/30">
        <div className="mx-auto max-w-7xl px-4 md:px-8">
          <p className="section-label mb-3"><span className="inline-block w-8 h-px bg-gold align-middle mr-3" />Visit Our Office</p>
          <h2 className="serif-display text-3xl md:text-4xl text-foreground mb-8">Todapur, New Delhi — India</h2>
          <div className="overflow-hidden rounded-md border border-border/60 aspect-[16/9] md:aspect-[21/9]">
            <iframe
              title="Hermanos Location"
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3502.8834569876826!2d77.16640287549533!3d28.63616897566367!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x390d030dc9db5495%3A0x2e05dab2a0b95c81!2sTodapur%2C%20New%20Delhi%2C%20Delhi%20110012!5e0!3m2!1sen!2sin!4v1700000000000"
              width="100%"
              height="100%"
              style={{ border: 0, filter: "grayscale(60%) contrast(1.05)" }}
              allowFullScreen=""
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />
          </div>
        </div>
      </section>

      <section className="py-16 border-t border-border/60 bg-navy text-white">
        <div className="mx-auto max-w-5xl px-4 md:px-8 text-center">
          <p className="section-label text-gold mb-3">Ready to grow?</p>
          <h3 className="serif-display text-3xl md:text-5xl text-white">Let's build a wholesale partnership.</h3>
          <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
            <Link to="/request-catalogue">
              <Button className="rounded-sm h-11 bg-gold hover:bg-gold-400 text-navy-900 font-semibold tracking-wider uppercase text-xs px-6">Request Catalogue <Send size={13} className="ml-2" /></Button>
            </Link>
            <Link to="/request-quotation">
              <Button variant="outline" className="rounded-sm h-11 border-white/30 hover:bg-white/10 text-white tracking-wider uppercase text-xs px-6">Request Quotation</Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
